import _ from 'underscore';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Stack, IconButton, ComboBox } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { tryParseTimeString, timeToString, preventEventPropagation } from '@bingads-webui-campaign-react/smart-component-util';

import {
  getTimeOptions,
  insertTimeOption,
  getTimeOptionsWithDisabledValue,
  isTimeOptionValid
} from './time-utils';

const getStyles = () => ({
  container: {
    margin: '10px 0px',
    '& .disabled-time-text-field .ms-TextField-fieldGroup::after': {
      border: 'none',
    },
  },
  separator: {
    padding: '6px 10px',
  },
  timeInputField: {
    width: 185,
  },
  removeButton: {
    border: 'none',
    background: 'none',
    height: 32,
    fontSize: 16,
  },
});

const defaultTime = { hour: 0, minute: 0 };

const styles = {
  optionsContainerWrapper: {
    maxHeight: 400,
  },
};

export const UnStyledTimeRangeEditor = ({
  classes,
  i18n,
  isDisabled,
  isRemovable,
  index,
  open,
  close,
  onChange,
  onRemove,
  currentDay,
  isCheckAllDay,
}) => {
  const timeFormat = 'h:mm';

  const [openTimeOptions, setOpenTimeOptions] = useState(getTimeOptions());
  const [closeTimeOptions, setCloseTimeOptions] = useState(getTimeOptions());
  const [openTimeSelectedKey, setOpenTimeSelectedKey] = useState();
  const [closeTimeSelectedKey, setCloseTimeSelectedKey] = useState();

  const disabledClassName = isDisabled ? 'disabled-time-text-field' : '';

  const buildChangeEventData = useCallback((timeOpen, timeClose) => {
    const openHour = timeOpen ? timeOpen.hour : null;
    const openMinute = timeOpen ? timeOpen.minute : null;
    const closeHour = timeClose ? timeClose.hour : null;
    const closeMinute = timeClose ? timeClose.minute : null;

    return {
      index,
      openHour,
      openMinute,
      closeHour,
      closeMinute,
      isValid: true,
    };
  }, [index]);

  // ComboBox in FluentUi has some issues:
  // When scroll a long list and don't click the option item, the selectedKey will get updated.
  // The solution is tell the difference of error scenario:
  // Combox onChange method will be called under three types of event: keydown, blur and click.
  // When the error scenario happens, the event type is "blur". The option value is not empty,
  // but the option.text !== event.target.value.
  const onOpenInputChange = (event, option, optionIndex, value) => {
    preventEventPropagation(event);
    // When user selects a option or types in a pre-defined value, option is available
    if (option) {
      const { type, target } = event;
      const { key, text } = option;

      // Handle the ComboxBox bug scenario
      if (type === 'blur' && target.value && target.value !== text) {
        return;
      }

      // If user selected a option, update selectedkey and timeslot in siteConfig.
      const openTime = tryParseTimeString(text);
      const closedOptionsWithDisabledValue = getTimeOptionsWithDisabledValue(
        closeTimeOptions,
        null,
        option
      );

      setOpenTimeSelectedKey(key);
      setCloseTimeOptions(closedOptionsWithDisabledValue);
      onChange(buildChangeEventData(openTime, close));
    } else if (value) {
      // If user typed desired time that doesn't exist in the option lists, add it.
      const openTime = tryParseTimeString(value);
      const currentCloseTime = tryParseTimeString(closeTimeSelectedKey);
      const isValidOpenTime = isTimeOptionValid(openTime, currentCloseTime, true);

      if (openTime && isValidOpenTime) {
        // 24 hours/ 12 hours => 12 hours
        const formattedTimeStr = timeToString(
          { hour: openTime.hour, minute: openTime.minute },
          timeFormat
        );
        const newOption = { key: formattedTimeStr, text: formattedTimeStr };
        const newOpenTimeOptions = insertTimeOption(openTimeOptions, newOption);
        const newCloseTimeOptions = insertTimeOption(closeTimeOptions, newOption);
        const closedOptionsWithDisabledValue = getTimeOptionsWithDisabledValue(
          newCloseTimeOptions,
          null,
          newOption
        );

        setOpenTimeOptions(newOpenTimeOptions);
        setOpenTimeSelectedKey(formattedTimeStr);
        setCloseTimeOptions(closedOptionsWithDisabledValue);
        onChange(buildChangeEventData(openTime, close));
      }
      // When user typed an invalid time string, just keep the previous one.
    }
  };

  const onCloseInputChange = (event, option, optionIndex, value) => {
    preventEventPropagation(event);
    // When user selects a option or types in a pre-defined value, option is available
    if (option) {
      const { key, text } = option;
      const { type, target } = event;

      // Handle the ComboxBox bug scenario
      if (type === 'blur' && target.value && target.value !== text) {
        return;
      }

      // If user selected a option, update selectedkey and timeslot in siteConfig.
      const closeTime = tryParseTimeString(text);
      const openOptionsWithDisabledValue = getTimeOptionsWithDisabledValue(
        openTimeOptions,
        option
      );

      setCloseTimeSelectedKey(key);
      setOpenTimeOptions(openOptionsWithDisabledValue);
      onChange(buildChangeEventData(open, closeTime));
    } else if (value) {
      // If user typed desired time that doesn't exist in the option lists, add it.
      const closeTime = tryParseTimeString(value);
      const currentOpenTime = tryParseTimeString(openTimeSelectedKey);
      const isValidCloseTime = isTimeOptionValid(closeTime, currentOpenTime, false);

      if (closeTime && isValidCloseTime) {
        // 24 hours/ 12 hours => 12 hours
        const formattedTimeStr = timeToString(
          { hour: closeTime.hour, minute: closeTime.minute },
          timeFormat
        );
        const newOption = { key: formattedTimeStr, text: formattedTimeStr };
        const newCloseTimeOptions = insertTimeOption(closeTimeOptions, newOption);
        const newOpenTimeOptions = insertTimeOption(openTimeOptions, newOption);
        const openOptionsWithDisabledValue = getTimeOptionsWithDisabledValue(
          newOpenTimeOptions,
          newOption
        );

        setCloseTimeOptions(newCloseTimeOptions);
        setCloseTimeSelectedKey(formattedTimeStr);
        setOpenTimeOptions(openOptionsWithDisabledValue);
        onChange(buildChangeEventData(open, closeTime));
      }
      // When user typed an invalid time string, just keep the previous one.
    }
  };

  const onRemoveClick = (e) => {
    preventEventPropagation(e);
    onRemove(index);
  };

  // Only trigger this hooks when isCheckAllDay changed and first render.
  // Do not add open, close and other props into dependencies otherwise it will
  // render repeatly.
  useEffect(() => {
    // update selectedKey and options.
    const openTimeStr = timeToString(open, timeFormat);
    const closeTimeStr = timeToString(close, timeFormat);
    const openTimeIndex = _.findIndex(openTimeOptions, option => option.text === openTimeStr);
    const closeTimeIndex = _.findIndex(closeTimeOptions, option => option.text === closeTimeStr);
    const openOption = { key: openTimeStr, text: openTimeStr };
    const closeOption = { key: closeTimeStr, text: closeTimeStr };

    // Since openTimeOptions and closeTimeOptions always has same number options,
    // any one would be fine
    let newTimeOptions = [...openTimeOptions];
    if (openTimeIndex === -1) {
      // open time is not pre-defined, add it into options.
      newTimeOptions = insertTimeOption(newTimeOptions, openOption);
    }

    if (closeTimeIndex === -1) {
      // close time is not pre-defined, add it into options.
      newTimeOptions = insertTimeOption(newTimeOptions, closeOption);
    }

    const openOptionsWithDisabledValue = getTimeOptionsWithDisabledValue(
      newTimeOptions,
      closeOption
    );
    const closedOptionsWithDisabledValue = getTimeOptionsWithDisabledValue(
      newTimeOptions,
      null,
      openOption
    );

    setCloseTimeOptions(closedOptionsWithDisabledValue);
    setOpenTimeOptions(openOptionsWithDisabledValue);
    setOpenTimeSelectedKey(openTimeStr);
    setCloseTimeSelectedKey(closeTimeStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckAllDay]);

  return (
    <div
      className={classNames(classes.container, 'time-range-editor')}
      data-index={index}
    >
      <Stack horizontal horizontalAlign="space-between">
        <ComboBox
          className={classNames(classes.timeInputField, 'open-time', disabledClassName)}
          allowFreeform
          useComboBoxAsMenuWidth
          autoComplete="on"
          styles={styles}
          disabled={isDisabled}
          selectedKey={openTimeSelectedKey}
          options={openTimeOptions}
          onChange={onOpenInputChange}
          aria-required="true"
          ariaLabel={
            i18n.getString(_TL_('Opening time {{number}} for {{day}}'), {
              number: index + 1,
              day: currentDay,
            })
          }
        />
        <span className={classes.separator}>{i18n.getString(_TL_('-'))}</span>
        <ComboBox
          className={classNames(classes.timeInputField, 'close-time', disabledClassName)}
          allowFreeform
          useComboBoxAsMenuWidth
          autoComplete="on"
          styles={styles}
          disabled={isDisabled}
          selectedKey={closeTimeSelectedKey}
          options={closeTimeOptions}
          onChange={onCloseInputChange}
          aria-required="true"
          ariaLabel={
            i18n.getString(_TL_('Closing time {{number}} for {{day}}'), {
              number: index + 1,
              day: currentDay,
            })
          }
        />
        {
          isRemovable && !isDisabled && (
            <span className="button-container">
              <IconButton
                className={classNames(classes.removeButton, 'remove-button')}
                onClick={onRemoveClick}
                iconProps={{ iconName: 'Cancel' }}
                ariaLabel={
                  i18n.getString(_TL_('Remove open hours {{number}} for {{day}}'), {
                    number: index + 1,
                    day: currentDay,
                  })}
              />
            </span>
          )
        }
      </Stack>
    </div>
  );
};

UnStyledTimeRangeEditor.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
    separator: PropTypes.string.isRequired,
    removeButton: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isRemovable: PropTypes.bool.isRequired,
  index: PropTypes.number,
  open: PropTypes.shape({
    hour: PropTypes.number,
    minute: PropTypes.number,
  }),
  close: PropTypes.shape({
    hour: PropTypes.number,
    minute: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  currentDay: PropTypes.string.isRequired,
  isCheckAllDay: PropTypes.bool.isRequired,
};

UnStyledTimeRangeEditor.defaultProps = {
  index: 0,
  open: defaultTime,
  close: defaultTime,
  onRemove: _.noop,
};

export const TimeRangeEditor = React.memo(withDefaultStyles(
  UnStyledTimeRangeEditor,
  getStyles
));
